import { path } from 'utils/object'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { streamScopedPathSchema } from './shared'

export enum ApplicationType {
  Granular = 'granular',
  Seeding = 'seeding',
  Liquid = 'liquid',
  Force = 'force',
  LinearPosition = 'linearposition',
  Pressure = 'pressure',
  Implement = 'implement',
  Global = 'global',
  Harvest = 'harvest'
}

export const productfulApplicationTypes = z.enum([
  ApplicationType.Granular,
  ApplicationType.Seeding,
  ApplicationType.Liquid
])
export type ProductfulApplications = z.infer<typeof productfulApplicationTypes>

export const applicationTypes = z.enum(ApplicationType)

export const controllableApplicationTypes = z.enum([
  ApplicationType.Granular,
  ApplicationType.Seeding,
  ApplicationType.Liquid,
  ApplicationType.Force,
  ApplicationType.LinearPosition,
  ApplicationType.Pressure
])

export type ControllableApplication = z.infer<typeof controllableApplicationTypes>

export const applicationTypesToUnits = {
  granular: {
    metric: 'kg/ha',
    imperial: 'lbs/ac'
  },
  seeding: {
    metric: 'seeds/ha',
    imperial: 'seeds/ac'
  },
  liquid: {
    metric: 'L/ha',
    imperial: 'gal/ac'
  },
  force: {
    metric: 'kg',
    imperial: 'lbs'
  },
  linearposition: {
    metric: 'cm',
    imperial: 'in'
  },
  pressure: {
    metric: 'kPa',
    imperial: 'psi'
  },
  harvest: {
    metric: 'bu/ha',
    imperial: 'bu/ac'
  }
} as const

/**
 * Constant application ID for "Equipment-Wide" data. Assumed stable for preemptive
 * requests for summary data, coverage, record layer, etc.
 */
export const GLOBAL_APPLICATION_ID = 'CAw'

/**
 * Constant layer ID that encodes the coverage data for a stream.
 * Assumed stable and predictable to facilitate preemptive requests for
 * extents and the first paint of the map.
 */
export const COVERAGE_LAYER_ID = 'Cghjb3ZlcmFnZQ'

/**
 * ID for a layer that encodes the record id for each pixel.
 *
 * This layer is not included in the response from the Applications endpoint,
 * but it is a special layer that is always present in the stream, and it
 * enables us to do tricks in the UI based on record ranges (e.g., highlighting
 * a range, showing a tooltip for a record, editing the stream to trim leading and
 * trailing records, etc.)
 */
export const RECORD_LAYER_ID = 'CghyZWNvcmRpZA'

const noLayersMessage = 'No layers found for system'

const applicationSchema = z.object({
  /**
   * Applications are grouped by type, which are categorized by the primary unit of
   * measure for the application. For example, a "granular" application is one that
   * is measured in pounds per acre, while a "liquid" application is measured in gallons
   * per acre.
   *
   * The global application is a special case that represents data that is not
   * associated with a specific application. This is used for equipment-wide data.
   *
   * All non-global applications can exist in multiple instances, although in practice,
   * seeding and harvest are usually one-per-stream.
   */
  type: z.object({
    /**
     * Identifier that is used to categorize the application type.
     */
    key: applicationTypes,
    /**
     * Human-readable name for the application type.
     */
    name: z.string()
  }),
  /**
   * Represents map layers that are associated with this application, each
   * of which corresponds to a given metric. The layers are ordered in descending
   * order of importance, with the most important layer first.
   *
   * An application always carries at least one layer.
   */
  layers: z
    .array(
      z.object({
        /**
         * Indicates whether the layer is a discrete layer. Discrete layers are
         * have a definite number of possible values, unlike continuous layers that
         * have floating-point values covering a full range from a min to a max.
         *
         * Note that continuous layers *can* contain discrete data (e.g., loss of ground
         * contact in a down force layer), but discrete layers are always and only discrete.
         */
        discrete: z.boolean(),
        /**
         * The unique identifier for the layer
         */
        id: z.string(),
        /**
         * Human-readable label for the layer
         */
        name: z.string(),
        /**
         * The category of the layer, which is (under the hood) a string enum that
         * says what type of data the layer represents. For example, "coverage" and
         * "dryyield" are two keys.
         *
         * Unlike the id, which is an identifier that may change between two streams
         * even when the data is the same type, this key allows stable comparison.
         */
        key: z.string()
      })
    )
    .min(1, noLayersMessage),
  application_id: z.string(),
  /**
   * The user-entered name of the application
   */
  name: z.string(),
  /**
   * Indicates whether the application is the "primary" application for the stream.
   * For example, in a harvest stream, the harvest application is almost certainly
   * primary and in spraying the primary application is the liquid one.
   *
   * For historical and technical reasons, there is no guarantee that a given stream
   * will have a primary application or that it will only have one. If there is more than
   * one, the first one wins, and if the stream has no primary application, the first
   * application in the array is considered primary.
   */
  default: z.boolean().optional()
})

const noApplicationsMessage = 'No systems found for pass'

const responseSchema = z.array(applicationSchema).min(1, noApplicationsMessage)

export const applicationsApi = defineApi({
  pathSchema: streamScopedPathSchema,
  querySchema: z.object({
    lastRecordId: z.number().optional()
  }),
  pathFor: (p) => {
    return path(`${p.requestType}/streams/${p.orgCode}/${p.streamId}/applications`, {
      lastRecordId: p.lastRecordId
    })
  },
  name: 'GetStreamApplications',
  description:
    "List the applications recorded on a stream (planting, spraying, harvesting, etc.) and the data layers each one carries — every layer as its id, human-readable name, stable key (e.g. 'coverage', 'dryyield'), and whether it is discrete. Layers are ordered most-important first. Use it to resolve the exact applicationId and layerId for a stream before switching the analysis view to a specific layer or a split-map comparison (SetAnalysisField), or before measuring a layer. It is a lookup, not an answer: it returns the layer catalog, not the underlying values — pivot to a summary or histogram tool to read actual numbers. Requires a streamId; resolve one from the field/pass catalog first if the user named a field.",
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema
})

/**
 * @group Routes
 *
 * @route
 * `GET /{stitch_prefix}/streams/{org_code}/{stream_id}/applications`
 *
 * @apiParam {stitch_prefix} prefix to stitch
 * @apiParam {org_code} organization/operation id
 * @apiParam {stream_id} stream id
 */
export type AvailableApplication = z.infer<typeof responseSchema>[number]

/**
 * The most important layer of the stream's primary application.
 */
export const defaultLayer = (applications: AvailableApplication[]) => {
  const primary = applications.find((a) => a.default) ?? applications[0]
  return { applicationId: primary.application_id, layerId: primary.layers[0].id }
}

/**
 * Checks if the given error is a ZodError that indicates that no applications were found.
 *
 * Given the current implementation of the applications endpoint, there can't be two errors
 * at once, but if that ever changes, this will return `true` if *any* of the issues are
 * related to an empty applications array.
 */
export const isEmptyApplicationsError = (error: unknown): error is z.ZodError =>
  error instanceof z.ZodError && error.issues.some((e) => e?.message === noApplicationsMessage)
